import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Boxes, GitPullRequest, ClipboardCheck } from 'lucide-react';
import { specsApi, type DocType, type DesignTemplate } from '@/services/specsApi';
import { cn } from '@/lib/utils';

const COPY = {
  requirement: { title: 'New Requirement', nameLabel: 'Requirement name', cta: 'Create Requirement', basePath: '/requirements' },
  design: { title: 'New Design Doc', nameLabel: 'Design doc name', cta: 'Create Design Doc', basePath: '/design' },
} as const;

// Keep in sync with the design templates offered by the capture wizard
// (SYSTEMDOX_DOC_TYPE_CONFIG) and registered in draft_api's ALL_TEMPLATES.
const DESIGN_TEMPLATES: { key: DesignTemplate; icon: typeof FileText; title: string; description: string }[] = [
  { key: 'adr', icon: FileText, title: 'Architecture Decision Record', description: 'Context, decision, and consequences — with a mermaid diagram.' },
  { key: 'design-doc', icon: Boxes, title: 'Design Document', description: 'Overview, architecture diagram, components, and trade-offs.' },
  { key: 'rfc', icon: GitPullRequest, title: 'RFC', description: 'Proposal for review: motivation, proposed approach, and alternatives.' },
  { key: 'test-plan', icon: ClipboardCheck, title: 'Test Plan', description: 'Scope, coverage matrix, entry/exit criteria, and risks.' },
];

export default function NewSpecPage({ docType = 'requirement' }: { docType?: Extract<DocType, 'requirement' | 'design'> }) {
  const copy = COPY[docType];
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [repo, setRepo] = useState('');
  const [template, setTemplate] = useState<DesignTemplate>('adr');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setLoading(true);
    setError('');
    try {
      const { draftId } = await specsApi.createSpec(
        name, repo, undefined, docType,
        docType === 'design' ? template : undefined,
      );
      navigate(`${copy.basePath}/${draftId}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create document');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="p-8 max-w-lg">
      <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-6">{copy.title}</h1>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">{error}</div>
        )}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">{copy.nameLabel}</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Dual-Path Authentication"
            required
            className="w-full px-3 py-2 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
          />
        </div>
        {docType === 'design' && (
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Template</label>
            <div className="grid gap-2 sm:grid-cols-2">
              {DESIGN_TEMPLATES.map(({ key, icon: Icon, title, description }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTemplate(key)}
                  aria-pressed={template === key}
                  className={cn(
                    'flex flex-col gap-1 rounded-lg border p-3 text-left transition-all',
                    template === key
                      ? 'border-blue-500 dark:border-blue-400 ring-1 ring-blue-500 dark:ring-blue-400'
                      : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-600',
                  )}
                >
                  <span className="flex items-center gap-1.5 text-sm font-medium text-slate-900 dark:text-slate-100">
                    <Icon className="w-4 h-4 text-slate-500" />
                    {title}
                  </span>
                  <span className="text-xs text-slate-500 dark:text-slate-400">{description}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Target repository (optional)</label>
          <input
            type="text"
            value={repo}
            onChange={(e) => setRepo(e.target.value)}
            placeholder="e.g. puglieseweb/product-systemdox"
            className="w-full px-3 py-2 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
          />
        </div>
        <button
          type="submit"
          disabled={loading || !name.trim()}
          className="w-full px-3 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? 'Creating...' : copy.cta}
        </button>
      </form>
    </div>
  );
}
